import React, { useEffect } from 'react';

import { useMatch } from 'react-router-dom';

import { observer } from 'mobx-react';

import { CircularProgress } from '@mui/material';

import Record from 'ui/Record/Record';

import MainHeader from 'components/MainHeader';

import { CenteredCircularProgress } from 'components/Progress';

import { Button } from 'components/Button';

import DataError from 'containers/Error/DataError';

import { formatMoment } from 'utils/formatters/timezone';

import { DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR } from 'core/constants';

import { useNavBarHeight } from 'utils/hooks/navbar';
import { useStore } from 'utils/hooks/store';

import { entitySubmissionConfig } from './config';
import { ButtonWrapper, Container, HeaderAction, LastPublished, LoadingContainer, SubmissionContainer } from './styles';
import SubmissionSection from './components/SubmissionSection';
import FormPreview from './components/FormPreview';
import RedirectModal from './components/RedirectModal';

const EntitiesFormSubmission: React.FunctionComponent = (): React.ReactElement => {
  const navBarHeight = useNavBarHeight();

  const eventsMatch = useMatch(entitySubmissionConfig.events.draftRoute);
  const auditsMatch = useMatch(entitySubmissionConfig.audits.draftRoute);

  const config = auditsMatch ? entitySubmissionConfig.audits : entitySubmissionConfig.events;
  // audits-only attributes: this container also serves Events drafts
  const isAudits = !!auditsMatch;
  const match = auditsMatch || eventsMatch;
  const draftId = match?.params?.draftId as string | undefined;

  const {
    routing,
    entitiesFormSubmission: {
      data,
      reset,
      isError,
      updating,
      isLoading,
      loadDraft,
      submitting,
      autoSaving,
      onNextClick,
      showPreview,
      onPreviousClick,
      requiredNotFilled,
      contentErrors,
      showRedirectModal,
      getCurrentSectionProperties,
    },
  } = useStore();

  useEffect(() => {
    if (draftId) {
      loadDraft(config.entityType, draftId);
    }
    return () => {
      reset();
    };
  }, [draftId]);

  const formName = data?.form.name || (isAudits ? 'New audit' : 'New Submission');

  const isDisabled = updating || autoSaving || requiredNotFilled.length > 0 || Object.keys(contentErrors).length > 0;

  if (!match) {
    return <DataError />;
  }

  return (
    <Record
      disableMaxContentHeight
      Header={
        <MainHeader
          title={formName}
          breadcrumbs={config.breadcrumbs(formName)}
          action={
            <HeaderAction>
              {autoSaving || submitting ? (
                <LoadingContainer data-testid={isAudits ? 'audit-fillout-autosave-status' : undefined}>
                  <CircularProgress size="2rem" sx={{ color: '#9E9E9E' }} />
                  <p>Saving changes...</p>
                </LoadingContainer>
              ) : (
                data?.updatedAt && (
                  <LastPublished
                    data-testid={isAudits ? 'audit-fillout-autosave-status' : undefined}
                  >{`Last saved: ${formatMoment(data?.updatedAt, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR)}`}</LastPublished>
                )
              )}
            </HeaderAction>
          }
        />
      }
      Content={() => {
        if (isLoading || updating || !data) {
          return <CenteredCircularProgress key="loading-progress" />;
        }

        if (isError) {
          return <DataError />;
        }

        if (!getCurrentSectionProperties()) {
          return <DataError />;
        }

        if (showPreview) {
          return <FormPreview />;
        }

        return (
          <SubmissionContainer>
            <Container>
              {updating ? <CenteredCircularProgress key="loading-progress" /> : <SubmissionSection />}
            </Container>
            <ButtonWrapper>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                data-testid={
                  isAudits ? (data.stack.length === 1 ? 'audit-fillout-cancel' : 'audit-fillout-back') : undefined
                }
                // Back replaces the whole draft from its own response, so it must wait for autosave.
                disabled={data.stack.length > 1 && autoSaving}
                onClick={() => {
                  if (data.stack.length === 1) {
                    routing.push(config.listRoute);
                  } else {
                    onPreviousClick();
                  }
                }}
              >
                {data.stack.length === 1 ? 'Cancel' : 'Back'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="large"
                data-testid={isAudits ? 'audit-fillout-next' : undefined}
                onClick={onNextClick}
                disabled={isDisabled}
              >
                Next
              </Button>
            </ButtonWrapper>
            <RedirectModal openModal={showRedirectModal} submissionNoun={config.submissionNoun} />
          </SubmissionContainer>
        );
      }}
      navBarHeight={navBarHeight}
    />
  );
};

export default observer(EntitiesFormSubmission);
